<div class="iot-tool" [formGroup]="form">
  <div class="iot-tool__header">
    <p class="iot-tool__subtitle">
      Device operations from the legacy desktop utility. Acts on the devices for this environment's
      IoT hub. Singular actions need one device selected; config push/download and log download can
      run against several at once. Destructive actions ask for confirmation.
    </p>
  </div>

  <!-- Device selection (searchable, multi-select) — selector on the left, recently-selected quick-pick to the right -->
  <section class="iot-tool__section iot-tool__section--device">
    <div class="iot-tool__device-row">
      <div class="iot-tool__device-left">
        <div class="custom-field">
          <label class="custom-field-label">Devices ({{ selectedSerials.length }} of {{ devices.length }} selected)</label>
          <!-- No "select all": fleets routinely exceed the 25-device cap, so "All" would just truncate.
               Users narrow the list by searching, then pick up to 25 individually. -->
          <synx-multi-select-dropdown
            formControlName="deviceSelection"
            [selectOptions]="deviceOptions"
            [searchEnableFlag]="true"
            [showSelectAllFlag]="false"
            [fullWidth]="true"
            [placeholder]="'Search by serial number, hardware ID or asset ID'"></synx-multi-select-dropdown>
        </div>

        <div class="iot-tool__chips" *ngIf="selectedSerials.length">
          <span class="iot-tool__chip" *ngFor="let s of selectedSerials">
            {{ deviceLabel(s) }}
            <button type="button" class="iot-tool__chip-remove" (click)="removeDevice(s)" aria-label="Remove">&times;</button>
          </span>
        </div>

        <button type="button" class="custom-button custom-button-stroked" (click)="reloadDevices()">Reload devices</button>
        <button type="button" class="custom-button custom-button-stroked" [disabled]="!hasSelection" (click)="clearSelection()">
          Clear selection
        </button>
      </div>

      <!-- Quick-pick: the last few devices added to a selection, listed for one-click re-selection without searching again. -->
      <div class="iot-tool__recent-panel" *ngIf="recentDevices.length">
        <span class="iot-tool__recent-label">Recent</span>
        <div class="iot-tool__recent-list">
          <span class="iot-tool__recent-chip" *ngFor="let r of recentDevices" [class.selected]="isSelected(r.serial)">
            <button
              type="button"
              class="iot-tool__recent-pick"
              [disabled]="isSelected(r.serial)"
              [title]="isSelected(r.serial) ? 'Already selected' : 'Add to selection'"
              (click)="selectRecent(r.serial)">
              {{ recentDeviceLabel(r) }}
            </button>
            <button
              type="button"
              class="iot-tool__chip-remove"
              (click)="removeRecentDevice(r.serial)"
              [attr.aria-label]="'Remove ' + recentDeviceLabel(r) + ' from recent'"
              title="Remove from recent">&times;</button>
          </span>
        </div>
        <button type="button" class="iot-tool__recent-clear" (click)="clearRecentDevices()" aria-label="Clear recent devices">
          Clear recent
        </button>
      </div>
    </div>
  </section>

  <!-- Result of the last operation (prominent) -->
  <section class="iot-tool__section iot-tool__result-panel" *ngIf="lastResult">
    <h3 class="iot-tool__section-title">
      Last operation: {{ lastResult.operation }}
      <span *ngIf="lastResult.pending" class="iot-tool__muted">&mdash; running&hellip;</span>
    </h3>
    <div class="iot-tool__overview" *ngIf="lastResult.rows.length">
      <table class="iot-tool__result-table">
        <thead>
          <tr>
            <th>Serial</th>
            <th>Asset ID</th>
            <th>Hardware ID</th>
            <th>Result</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          <ng-container *ngFor="let row of lastResult.rows">
            <tr>
              <td>{{ row.serialNumber }}</td>
              <td>{{ assetIdFor(row.serialNumber) }}</td>
              <td>{{ hardwareIdFor(row.serialNumber) }}</td>
              <td>
                <span class="iot-tool__dot" [class.online]="row.success" [class.offline]="!row.success"></span>
                {{ row.success ? 'OK' : 'Failed' }}
              </td>
              <td>{{ row.detail }}</td>
            </tr>
            <!-- The device's response (e.g. shell command output), expandable so large output like smartctl stays readable. -->
            <tr *ngIf="row.response" class="iot-tool__response-row">
              <td colspan="5">
                <details class="iot-tool__response">
                  <summary>Response</summary>
                  <pre class="iot-tool__response-body">{{ formatResponse(row.response) }}</pre>
                </details>
              </td>
            </tr>
          </ng-container>
        </tbody>
      </table>
    </div>
  </section>

  <div class="iot-tool__grid">
    <!-- Device info (single device) -->
    <section class="iot-tool__section">
      <h3 class="iot-tool__section-title">Device info</h3>

      <!-- Single device: full detail -->
      <ng-container *ngIf="singleSelected && selectedDevice">
        <dl class="iot-tool__info">
          <dt>Asset ID</dt>
          <dd>{{ assetId }}</dd>
          <dt>Serial number</dt>
          <dd>{{ selectedDevice.serialNumber }}</dd>
          <dt>Hardware ID</dt>
          <dd>{{ hardwareId }}</dd>
          <dt>Primary hardware</dt>
          <dd>{{ selectedDevice.hardware || '—' }}</dd>
          <dt>Primary firmware</dt>
          <dd>{{ selectedDevice.firmware || '—' }}</dd>
          <ng-container *ngIf="selectedDevice.hardwareVersion">
            <dt>Primary hardware version</dt>
            <dd>{{ selectedDevice.hardwareVersion }}</dd>
          </ng-container>
          <dt>Secondary hardware</dt>
          <dd>{{ selectedDevice.slaveDevice || '—' }}</dd>
          <ng-container *ngIf="selectedDevice.slaveVersion">
            <dt>Secondary firmware</dt>
            <dd>{{ selectedDevice.slaveVersion }}</dd>
          </ng-container>
          <dt>Status</dt>
          <dd>
            <span *ngIf="isPinging(selectedDevice.serialNumber)" class="iot-tool__muted">Pinging&hellip;</span>
            <ng-container *ngIf="!isPinging(selectedDevice.serialNumber) && statusFor(selectedDevice.serialNumber) as st">
              <span
                class="iot-tool__dot"
                [class.online]="st.online"
                [class.offline]="!st.online && !st.indeterminate"
                [class.indeterminate]="st.indeterminate"
                [title]="statusTooltip(selectedDevice.serialNumber)"></span>
              {{ st.indeterminate ? 'Unknown' : (st.online ? 'Online' : 'Offline') }}
            </ng-container>
            <span *ngIf="!isPinging(selectedDevice.serialNumber) && !statusFor(selectedDevice.serialNumber)" class="iot-tool__muted">&mdash;</span>
          </dd>
          <ng-container *ngIf="statusFor(selectedDevice.serialNumber) as st">
            <!-- When the status was last checked, so it's clear whether it may be stale (press Refresh). -->
            <dt>Last checked</dt>
            <dd>{{ st.lastPinged | date: 'dd-MMM-yyyy HH:mm:ss' }}</dd>
            <ng-container *ngIf="!st.online && selectedDevice.lastPinged">
              <dt>Last seen</dt>
              <dd>{{ selectedDevice.lastPinged | date: 'dd-MMM-yyyy HH:mm:ss' }}</dd>
            </ng-container>
          </ng-container>
        </dl>
        <button type="button" class="custom-button custom-button-stroked" (click)="pingSelected()">Refresh</button>
      </ng-container>

      <!-- Multiple devices: compact overview -->
      <ng-container *ngIf="hasSelection && !singleSelected">
        <p class="iot-tool__muted">{{ selectedSerials.length }} devices selected.</p>
        <div class="iot-tool__overview">
          <table class="iot-tool__result-table">
            <thead>
              <tr>
                <th>Serial</th>
                <th>Asset ID</th>
                <th>Primary HW</th>
                <th>Firmware</th>
                <th>Secondary HW</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let d of selectedDevices">
                <td>{{ d.serialNumber }}</td>
                <td>{{ d.fleetId || '—' }}</td>
                <td>{{ d.hardware || '—' }}</td>
                <td>{{ d.firmware || '—' }}</td>
                <td>{{ d.slaveDevice || '—' }}</td>
                <td>
                  <!-- Dot only here to keep the multi-device table compact; the tooltip carries the wording. -->
                  <span *ngIf="isPinging(d.serialNumber)" class="iot-tool__muted">&hellip;</span>
                  <ng-container *ngIf="!isPinging(d.serialNumber) && statusFor(d.serialNumber) as st">
                    <span
                      class="iot-tool__dot"
                      [class.online]="st.online"
                      [class.offline]="!st.online && !st.indeterminate"
                      [class.indeterminate]="st.indeterminate"
                      [title]="statusTooltip(d.serialNumber)"></span>
                  </ng-container>
                  <span *ngIf="!isPinging(d.serialNumber) && !statusFor(d.serialNumber)" class="iot-tool__muted">&mdash;</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <button type="button" class="custom-button custom-button-stroked" (click)="pingSelected()">Refresh</button>
      </ng-container>

      <!-- Nothing selected -->
      <p class="iot-tool__muted" *ngIf="!hasSelection">Select a device to see its details and live status.</p>

      <hr />
      <button type="button" class="custom-button custom-button-flat" [disabled]="!singleSelected" (click)="getFirmwareStatus()">
        Firmware update status
      </button>
      <div *ngIf="firmwareStatus" class="iot-tool__result">
        <div><strong>Status:</strong> {{ firmwareStatus.status || 'unknown' }}</div>
        <div *ngIf="firmwareStatus.lastUpdated"><strong>Last updated:</strong> {{ firmwareStatus.lastUpdated }}</div>
      </div>

      <hr />
      <button type="button" class="custom-button custom-button-stroked" [disabled]="!singleSelected" (click)="toggleDeviceKey()">
        {{ deviceKey ? 'Hide device keys' : 'Reveal device keys (this environment)' }}
      </button>
      <p class="iot-tool__muted">
        These are the keys on the current environment. To move a unit you need the <em>target</em> environment's
        key — reveal that in the target environment's own IoT Tool.
      </p>
      <div *ngIf="deviceKey" class="iot-tool__result">
        <div><strong>Primary:</strong> <code>{{ deviceKey.primaryKey }}</code></div>
        <div><strong>Secondary:</strong> <code>{{ deviceKey.secondaryKey }}</code></div>
      </div>
    </section>

    <!-- Commands (single device) -->
    <section class="iot-tool__section" [class.iot-tool__section--disabled]="!singleSelected">
      <h3 class="iot-tool__section-title">Device commands</h3>
      <button type="button" class="custom-button custom-button-warn" [disabled]="!singleSelected" (click)="revertDefaults()">Revert to defaults</button>

      <div class="custom-field">
        <label class="custom-field-label">Simulate failure (bitfield)</label>
        <custom-text-input inputId="sim-bitfield" formControlName="simulateBitfield" type="number" placeholder="0"></custom-text-input>
      </div>
      <button type="button" class="custom-button custom-button-warn" [disabled]="!singleSelected" (click)="simulateFailure()">Send</button>

      <div class="custom-field">
        <label class="custom-field-label">Shell command</label>
        <synx-dropdown
          formControlName="shellCommandPreset"
          [selectOptions]="shellCommandPresets"
          [fullWidth]="true"
          [placeholder]="'Pick a common command…'"></synx-dropdown>
        <custom-text-input inputId="shell-cmd" formControlName="shellCommand" placeholder="e.g. ls -la"></custom-text-input>
      </div>
      <button type="button" class="custom-button custom-button-warn" [disabled]="!singleSelected" (click)="sendShellCommand()">Run command</button>

      <div class="custom-field">
        <label class="custom-field-label">GPS IoT batch size</label>
        <custom-text-input inputId="gps-batch" formControlName="gpsBatchSize" type="number" placeholder="1"></custom-text-input>
      </div>
      <button type="button" class="custom-button custom-button-flat" [disabled]="!singleSelected" (click)="updateGpsBatchSize()">Send</button>
    </section>

    <!-- Teltonika configuration (single device) -->
    <section class="iot-tool__section" [class.iot-tool__section--disabled]="!singleSelected">
      <h3 class="iot-tool__section-title">Teltonika configuration</h3>
      <div class="custom-field">
        <label class="custom-field-label">Target IP</label>
        <custom-text-input inputId="emu-target-ip" formControlName="emulatorTargetIp" placeholder=""></custom-text-input>
      </div>
      <div class="custom-field">
        <label class="custom-field-label">Data IP</label>
        <custom-text-input inputId="emu-data-ip" formControlName="emulatorDataIp" placeholder=""></custom-text-input>
      </div>
      <div class="custom-field">
        <label class="custom-field-label">Target type</label>
        <synx-dropdown
          formControlName="emulatorTargetType"
          [selectOptions]="targetTypeOptions"
          [fullWidth]="true"
          [placeholder]="'Select target type'"></synx-dropdown>
      </div>
      <div class="custom-field">
        <label class="custom-field-label">Offline timeout (mins)</label>
        <custom-text-input inputId="emu-timeout" formControlName="emulatorOfflineTimeout" type="number" placeholder="5"></custom-text-input>
      </div>
      <button type="button" class="custom-button custom-button-flat" [disabled]="!singleSelected" (click)="setEmulatorConfig()">Send Teltonika config</button>
    </section>

    <!-- Move device to another environment (single device) -->
    <section class="iot-tool__section" [class.iot-tool__section--disabled]="!singleSelected">
      <h3 class="iot-tool__section-title">Move device to another environment</h3>
      <p class="iot-tool__muted" *ngIf="currentHub?.name">Current environment: {{ currentHub.name }}</p>
      <p class="iot-tool__muted">
        The unit must already be created in the target environment. Read its API key and device key from the
        target environment's own IoT Tool, then do both steps below in order: send the API key while the unit
        is still here, then move it.
      </p>
      <div class="custom-field">
        <label class="custom-field-label">Target environment</label>
        <synx-dropdown
          formControlName="hubTarget"
          [selectOptions]="targetEnvironmentOptions"
          [fullWidth]="true"
          [placeholder]="'Select environment'"></synx-dropdown>
      </div>

      <p class="iot-tool__muted"><strong>Step 1 — send the target API key</strong> (while the unit is still on this hub)</p>
      <div class="custom-field">
        <label class="custom-field-label">API key on target environment</label>
        <custom-text-input inputId="hub-api-key" formControlName="hubApiKey" placeholder=""></custom-text-input>
      </div>
      <button type="button" class="custom-button custom-button-flat" [disabled]="!singleSelected" (click)="sendApiKeyToTarget()">Send API key</button>

      <p class="iot-tool__muted"><strong>Step 2 — move to the target hub</strong></p>
      <div class="custom-field">
        <label class="custom-field-label">Device key on target environment</label>
        <custom-text-input inputId="hub-key" formControlName="hubKey" placeholder=""></custom-text-input>
      </div>
      <button type="button" class="custom-button custom-button-warn" [disabled]="!singleSelected" (click)="reassignHub()">Move device</button>
    </section>

    <!-- Download logs (bulk-capable) -->
    <section class="iot-tool__section" [class.iot-tool__section--disabled]="!hasSelection">
      <h3 class="iot-tool__section-title">Download logs <span class="iot-tool__tag iot-tool__tag--bulk">bulk</span></h3>
      <p class="iot-tool__muted">
        Also available on Asset Management. Requests logs from the selected device(s) and downloads them
        once uploaded (one file per device).
      </p>
      <button type="button" class="custom-button custom-button-flat" [disabled]="!hasSelection || bulkPending" (click)="downloadLogs()">
        {{ bulkPending ? 'Working…' : 'Download logs' }}
      </button>
    </section>

    <!-- Device configuration: push a stored config to devices, or pull the live config from them -->
    <section class="iot-tool__section" [class.iot-tool__section--disabled]="!hasSelection">
      <h3 class="iot-tool__section-title">
        Device configuration <span class="iot-tool__tag iot-tool__tag--bulk">bulk</span>
      </h3>
      <p class="iot-tool__muted">Push a stored configuration to the selected device(s), or pull the current configuration from them.</p>

      <div class="custom-field">
        <label class="custom-field-label">Push configuration to device(s)</label>
        <synx-dropdown
          formControlName="selectedConfigFile"
          [selectOptions]="configOptions"
          [fullWidth]="true"
          [placeholder]="'Select configuration'"></synx-dropdown>
      </div>
      <button type="button" class="custom-button custom-button-stroked" (click)="loadConfigFiles()">Reload</button>
      <button type="button" class="custom-button custom-button-flat" [disabled]="!hasSelection || bulkPending" (click)="pushConfig()">Push to device(s)</button>

      <hr />

      <label class="custom-field-label">Download configuration from device(s)</label>
      <p class="iot-tool__muted" *ngIf="selectionHasTeltonika">
        Configuration can't be downloaded from a Teltonika unit — deselect it to continue.
      </p>
      <div>
        <button
          type="button"
          class="custom-button custom-button-flat"
          [disabled]="!hasSelection || bulkPending || selectionHasTeltonika"
          [title]="selectionHasTeltonika ? 'Configuration cannot be downloaded from a Teltonika unit' : ''"
          (click)="downloadConfig()">
          {{ bulkPending ? 'Working…' : 'Download configuration' }}
        </button>
      </div>

      <hr />

      <label class="custom-field-label">Update firmware</label>
      <p class="iot-tool__muted">Firmware updates to devices are performed on the Asset Management page.</p>
      <button type="button" class="custom-button custom-button-stroked" (click)="goToAssetManagement()">
        Open Asset Management &rarr;
      </button>
    </section>
  </div>

  <!-- Cloud storage: one full-width card with the three blob-storage actions (device-independent) -->
  <section class="iot-tool__section iot-tool__section--wide">
    <h3 class="iot-tool__section-title">Firmware &amp; Configuration storage</h3>
    <p class="iot-tool__muted">Upload or remove the firmware and configuration files held in cloud storage.</p>

    <div class="iot-tool__storage-grid">
      <div class="iot-tool__storage-col">
        <div class="custom-field">
          <label class="custom-field-label">Upload firmware ({{ firmwareExtensionsLabel }})</label>
          <input type="file" class="iot-tool__file" [attr.accept]="firmwareAccept" (change)="onFirmwareSelected($event)" />
        </div>
        <div class="iot-tool__storage-actions">
          <button type="button" class="custom-button custom-button-flat" (click)="uploadFirmware()">Upload firmware</button>
        </div>
      </div>

      <div class="iot-tool__storage-col">
        <div class="custom-field">
          <label class="custom-field-label">Delete firmware</label>
          <synx-dropdown
            formControlName="selectedFirmwareFile"
            [selectOptions]="firmwareOptions"
            [fullWidth]="true"
            [placeholder]="'Select firmware'"></synx-dropdown>
        </div>
        <div class="iot-tool__storage-actions">
          <button type="button" class="custom-button custom-button-stroked" (click)="loadFirmwareFiles()">Reload</button>
          <button type="button" class="custom-button custom-button-warn" (click)="deleteFirmware()">Delete selected</button>
        </div>
      </div>

      <div class="iot-tool__storage-col">
        <div class="custom-field">
          <label class="custom-field-label">Upload configuration ({{ configExtensionsLabel }})</label>
          <input type="file" class="iot-tool__file" [attr.accept]="configAccept" (change)="onConfigSelected($event)" />
        </div>
        <div class="iot-tool__storage-actions">
          <button type="button" class="custom-button custom-button-flat" (click)="uploadConfig()">Upload config</button>
        </div>
      </div>
    </div>
  </section>
</div>
